import { get } from "lodash";
import { HttpStatusCode } from "../../../../common/http-status-code";
import { getAxiosPyrgosConfig } from "../../../../common/pyrgos";

import { Mapper } from "../../../../../lib/utils/mapper";
import { AuthenticationService } from "../../../../services/authentication-service/authentication.service";
import type { IAuthenticationService } from "../../../../services/authentication-service/authentication.service.interface";
import { AiChatbotChunkDTO } from "../../../../services/server-side-events-ai-chatbot/dtos/ai-chatbot-chunk.dto";
import { AiChatbotChunkEntity } from "../../../../services/server-side-events-ai-chatbot/entities/ai-chatbot-chunk.entity";
import { QuotaExceededError } from "../../errors/quota-exceeded.error";
import { RateLimitExceededError } from "../../errors/rate-limit-exceeded.error";
import { StreamClosedSuccessfully } from "../../errors/stream-closed-succefully.error";
import { StreamClosedWithError } from "../../errors/stream-closed-with.error";
import type { IStreamMessageDataSource } from "./stream-message.datasource.interface";

export class StreamMessageDataSource implements IStreamMessageDataSource {
  private readonly _CLOSE_CONNECTION_MESSAGE_TEXT = "Stream ended successfully";

  private readonly _baseUrl: string = getAxiosPyrgosConfig().baseURL;
  private readonly _config: EventSourceInit = {
    withCredentials: true,
  };

  constructor(
    private _eventSource?: EventSource,
    private _logoutService: IAuthenticationService = new AuthenticationService()
  ) {}

  public listen(eventCallback: (message: AiChatbotChunkEntity) => void, errorCallback: (message: Error) => void): void {
    if (!this._eventSource) {
      const url = `${this._baseUrl}/assistants/ask-within-session/stream`;
      this._eventSource = new EventSource(url, this._config);
    }

    this._eventSource.onmessage = this._onMessage.bind(this, eventCallback);
    this._eventSource.onerror = this._onError.bind(this, errorCallback);
  }

  public close(): void {
    if (this._eventSource) {
      this._eventSource.close();
      this._eventSource = undefined;
    }
  }

  private _onMessage(
    this: StreamMessageDataSource,
    eventCallback: (message: AiChatbotChunkEntity) => void,
    event: MessageEvent
  ): void {
    const parsedData = JSON.parse(event.data);
    const messageDto = Mapper.mapData(AiChatbotChunkDTO, parsedData);
    const messageEntity = Mapper.mapData(AiChatbotChunkEntity, messageDto);
    eventCallback(messageEntity);
  }

  private async _onError(
    this: StreamMessageDataSource,
    errorCallback: (event: Error) => void,
    event: Event
  ): Promise<void> {
    this._eventSource?.close();
    this._eventSource = undefined;

    let errorMessage: Error = new StreamClosedWithError();
    const fallbackDataError = "unknown error";
    const indexWithMessage = "data";
    const eventData: string = get(event, indexWithMessage, fallbackDataError);

    const isUnauthorized = this._isUnauthorized(eventData);

    if (isUnauthorized) {
      await this._logoutService.logoutAndResetStorage();
      return;
    }

    const isRateLimitExceeded = RateLimitExceededError.isRateLimitExceeded(eventData);
    const isQuotaExceeded = QuotaExceededError.isQuotaExceeded(eventData);
    if (isRateLimitExceeded || isQuotaExceeded) {
      errorMessage = RateLimitExceededError.create();
      errorCallback(errorMessage);
      return;
    }

    if (eventData === this._CLOSE_CONNECTION_MESSAGE_TEXT) {
      errorMessage = new StreamClosedSuccessfully();
    }

    errorCallback(errorMessage);
  }

  private _isUnauthorized(eventData: string): boolean {
    const lowerCaseEventData = eventData.toLowerCase();
    const isUnauthorized =
      lowerCaseEventData.includes("unauthorized") ||
      lowerCaseEventData.includes(HttpStatusCode.UNAUTHORIZED.toString());

    return isUnauthorized;
  }
}
